/**
 * service_report_controller.js — send the per-visit report from a job page.
 *
 * The endpoint answers 202 and hands the send to a background worker, so there
 * is nothing to render back: this closes the modal and says the report is on
 * its way. The outcome the user actually cares about — delivered or bounced —
 * arrives later through the usual notification path, not here.
 *
 * A 403 is the one failure worth interrupting for: it means the business has
 * not verified the address it sends from, and no amount of retrying will fix
 * that from this modal. Its message comes from the server so the wording stays
 * in one place (application/services/email_verification.py).
 *
 * data-controller="service-report" hosts on the form inside
 * templates/components/modals/send_service_report_modal.html.
 */

import { Controller } from "@hotwired/stimulus";

export default class extends Controller {
    static get targets() { return ['submit']; }

    connect() {
        this._onSubmit = (event) => this.send(event);
        this.element.addEventListener('submit', this._onSubmit);
    }

    disconnect() {
        this.element.removeEventListener('submit', this._onSubmit);
        this._onSubmit = null;
    }

    async send(event) {
        event.preventDefault();
        this._setBusy(true);
        try {
            const response = await fetch(this.element.action, {
                method: 'POST',
                body: new FormData(this.element),
                headers: { 'Accept': 'application/json' },
                credentials: 'same-origin',
            });
            const payload = await response.json().catch(() => ({}));
            this._report(response.ok, payload.message);
            if (response.ok) {
                this._close();
                // Shows the report's header line and Activity row. The page
                // check, not a Turbo visit: the saved copy predates the send.
                window.chippiePageCheck?.refresh();
            }
        } catch {
            // A network failure only: the endpoint's own refusals arrive as a
            // status code above, so there is nothing in the throw worth showing.
            this._report(false, 'Could not reach the server. Try again when you are back online.');
        } finally {
            this._setBusy(false);
        }
    }

    _setBusy(busy) {
        if (this.hasSubmitTarget) this.submitTarget.disabled = busy;
    }

    _report(ok, message) {
        if (typeof window.showNotification !== 'function') return;
        window.showNotification(message || (ok ? 'Sending service report...' : 'Could not send the report.'),
                                ok ? 'success' : 'error');
    }

    _close() {
        const modal = this.element.closest('.modal');
        if (!modal || !window.bootstrap) return;
        const instance = window.bootstrap.Modal.getInstance(modal);
        if (instance) instance.hide();
    }
}
